import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Button, TextField, Alert } from '@mui/material';
import OtpQrCode from '@/component/otp/OtpQrCode';
import { enableOtp, verifyOtp } from '@/api/asset/admin/read';
import { SUCCESS } from '@/lib/common/name-space';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { walletState } from '@/atom/wallet';
import { ReadVerifyOtpType } from '@/types';

interface OtpLoginResponse {
  qrUrl: string;
  secret: string;
}
export default function OtpLoginPage({
  isOtpEnabled,
  otpData,
  id,
}: {
  isOtpEnabled: number;
  otpData: OtpLoginResponse | null;
  id: string;
}) {
  const [otpCode, setOtpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [, setWallet] = useRecoilState(walletState);

  const navigate = useNavigate();
  // 서버에서 OTP 데이터 가져오기
  // const fetchOtpData = async () => {
  //   setIsLoading(true);
  //   setError(null);

  //   try {
  //     const response = await otpLogin();
  //     setOtpData(response.data);
  //   } catch (err) {
  //     setError('OTP 데이터를 가져오는데 실패했습니다.');
  //     console.error('OTP 데이터 가져오기 실패:', err);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  // OTP 코드 검증
  const verifyOtpCode = async () => {
    if (!otpCode.trim()) {
      setError('OTP 코드를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const readVerifyOtpType: ReadVerifyOtpType = {
        id,
        otpCode: otpCode,
      };
      const re = await verifyOtp(readVerifyOtpType);
      console.log(re);
      if (re.status === SUCCESS) {
        setSuccess('OTP 인증이 성공했습니다!');
        navigate('/dashboard');
        setWallet(true);
        // 코드 넣기
        sessionStorage.setItem('code', re.data.code);
        sessionStorage.setItem('loginId', id);
        // 로그인 성공 후 리다이렉트 로직 추가
        // 예: router.push('/dashboard');
      } else {
        setError(re.data.message || 'OTP 인증에 실패했습니다.');
      }
    } catch (err) {
      setError('OTP 검증에 실패했습니다.');
      console.error('OTP 검증 실패:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '10vh',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            OTP 인증
          </Typography>

          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            보안을 위해 2단계 인증을 진행합니다.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>로딩 중...</Typography>
            </Box>
          ) : isOtpEnabled === 0 && otpData ? (
            <>
              {/* QR 코드 표시 */}
              <OtpQrCode
                qrUrl={otpData.qrUrl}
                secret={otpData.secret}
                title="Google Authenticator 설정"
                description="Google Authenticator 앱에서 QR 코드를 스캔하거나 Secret Key를 수동으로 입력하세요."
              />
              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    setIsLoading(true);
                    setError(null);

                    const response = await enableOtp(id);

                    if (response.status === SUCCESS) {
                      setSuccess('OTP가 성공적으로 활성화되었습니다!');
                      // 성공 후 잠시 대기 후 페이지 이동
                      setTimeout(() => {
                        navigate('/');
                      }, 1500);
                    } else {
                      setError(response.data?.message || 'OTP 활성화에 실패했습니다.');
                    }
                  } catch (err) {
                    setError('OTP 활성화 중 오류가 발생했습니다.');
                    console.error('OTP 활성화 실패:', err);
                  } finally {
                    setIsLoading(false);
                  }
                }}
                disabled={isLoading}
                fullWidth
                size="large"
              >
                {isLoading ? 'OTP 활성화 중...' : 'QR CODE 스켄 후 클릭'}
              </Button>
            </>
          ) : (
            <>
              {/* OTP 코드 입력 */}
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  OTP 코드 입력
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  OTP 앱에서 생성된 6자리 코드를 입력하세요.
                </Typography>

                <TextField
                  fullWidth
                  label="OTP 코드"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isLoading && otpCode.trim()) {
                      e.preventDefault();
                      verifyOtpCode();
                    }
                  }}
                  placeholder="000000"
                  inputProps={{
                    maxLength: 6,
                    pattern: '[0-9]*',
                  }}
                  sx={{ mb: 2 }}
                />

                <Button
                  variant="contained"
                  onClick={verifyOtpCode}
                  disabled={isLoading || !otpCode.trim()}
                  fullWidth
                  size="large"
                >
                  {isLoading ? '검증 중...' : '인증하기'}
                </Button>
              </Box>
              {/* <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>OTP 데이터를 불러오는 중...</Typography>
              <Button variant="outlined" onClick={fetchOtpData} sx={{ mt: 2 }}>
              다시 시도
            </Button>
            </Box> */}
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
}
